import {
  Box,
  Card,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ItemCreation } from '~/Components';
import { PrivateLayout } from '~/Layout/PrivateLayout';
import { DropArea } from '../Components/DropArea';
import { ExtendedVoD } from './ExtendedVoD.model';
import { useVideoOnDemandEdit } from './useVideoOnDemandEdit';
import useVideoOnDemandUploadVideo from './useVideoOnDemandUploadVideo';
import { useRequestUpdateThumbnailVOD } from '~/requests/videoOnDemandService/useRequestUpdateThumbnailVOD';
import { useRequestPatchVOD } from '~/requests/videoOnDemandService/useRequestPatchVOD';
import { VoDPreview } from './VideoOnDemandPreview';
import { useNavigation, useProvider, useToastMessage, useNavigationContext } from '~/Hooks';
import { Route } from '~/Constants/Routes.constants';
import { routeReplacer } from '~/Helpers/String.helper';
import { makeStyles } from '~/UIKit/Style';
import { Button } from '~/UIKit';

const useStyles = makeStyles((theme) => ({
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    margin: '20px 0',
  },
  loadingCar: {
    position: 'relative',
    width: '100%',
    maxWidth: '400px',
    height: '6px',
    backgroundColor: '#e0e0e0' /* Light gray background for the progress bar */,
    borderRadius: '3px',
    overflow: 'hidden',
  },
  loadingIndicator: {
    height: '100%',
    backgroundColor: '#007bff' /* Blue color for the progress bar */,
    borderRadius: '3px 0 0 3px' /* Rounded corners on the left */,
    transition: 'width 0.3s ease' /* Smooth transition for width changes */,
  },
  uploading: {
    marginTop: '10px',
    fontSize: '14px',
    color: '#555' /* Dark gray text color */,
    textAlign: 'center',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '16px',
  },
  subtitle: {
    fontSize: '18px',
    fontWeight: '500',
    marginBottom: '12px',
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: '16px',
  },
  cardPadding: {
    padding: '32px 70px',
    [theme.breakpoints.down('sm')]: {
      padding: 4,
    },
  },
}));

export const VideoOnDemandEdit = () => {
  const { t } = useTranslation();
  const { currentProvider } = useProvider();
  const { navigate, getParams } = useNavigation();
  const { id: videoEditId } = getParams();
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showToast } = useToastMessage();
  const { classes } = useStyles();
  const { blockNavigation } = useNavigationContext();
  const { updateVideo } = useRequestPatchVOD();
  const { updateThumbnail, vodThumbnailResponse } = useRequestUpdateThumbnailVOD();
  const {
    listItems,
    onSave,
    initialValues,
    validate,
    error,
    isLoading,
    isSubmitDisabled,
    onValidationErrors,
    vodCreateResponse,
    saveVideoFormData,
    currentVideo,
    isVodVideoCreateSuccess,
    thumbnailUrl,
  } = useVideoOnDemandEdit({ id: videoEditId });

  const {
    handleFileUpload,
    uploadProgress,
    isUploading,
    uploadError,
  } = useVideoOnDemandUploadVideo();

  const videoId =
    vodCreateResponse?.id && uploadProgress === 100 ? vodCreateResponse.id : videoEditId;

  useEffect(() => {
    if (vodThumbnailResponse?.url) {
      showToast('app.vod.thumbnailUpdated', t('app.vod.thumbnailUpdated'));
      saveVideoFormData({ ...currentVideo, thumbnailPosition: vodThumbnailResponse?.position });
      validate({ ...currentVideo, thumbnailPosition: vodThumbnailResponse?.position });
    }
  }, [vodThumbnailResponse?.url]);

  const handleUploadVideo = (file: File | null) => {
    if (file) {
      setFileToUpload(file);
      onSave(file.name);
    }
  };

  const steps = [t('app.vod.generalinfo'), t('app.vod.upload'), t('app.vod.pickthumbnail')];

  const handleNext = () => {
    let step = 1;
    if (videoEditId && activeStep === 0) {
      step = 2;
      blockNavigation(false);
    }
    setActiveStep((prevActiveStep) => prevActiveStep + step);
  };

  const handleBack = () => {
    let step = 1;
    if (videoEditId && activeStep === 2) {
      step = 2;
    }
    setActiveStep((prevActiveStep) => prevActiveStep - step);
  };

  const handleSaveThumbnail = (thumbnailPosition: number) => {
    if (videoId) {
      updateThumbnail({ videoId, position: thumbnailPosition, width: 296 });
    }
  };

  const handleNavigateToVODList = () => {
    updateVideo(currentVideo as any);
    navigate(routeReplacer(Route.VIDEO_ON_DEMAND, { domain: currentProvider?.domain || '' }));
  };

  const handleSave = () => {
    switch (activeStep) {
      case 0:
        saveVideoFormData(currentVideo);
        handleNext();
        break;
      case 1:
        handleNext();
        break;

      case 2:
        handleNavigateToVODList();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (isVodVideoCreateSuccess && vodCreateResponse?.id && fileToUpload) {
      handleFileUpload(fileToUpload, vodCreateResponse.id);
    }
  }, [isVodVideoCreateSuccess, vodCreateResponse]);

  useEffect(() => {
    if (uploadProgress === 100) {
      handleNext();
    }
  }, [uploadProgress]);

  const thumbnailUrlFromResponse = vodThumbnailResponse?.url || thumbnailUrl;
  return (
    <PrivateLayout
      title={t('app.vod.create')}
      actionDisabled={isSubmitDisabled}
      actionName={activeStep !== 2 ? t('app.next') : t('app.save')}
      onActionTaken={handleSave}
      secondaryTitleActionElement={
        activeStep !== 0 && !isMobile ? <Button onClick={handleBack}>{t('app.back')}</Button> : null
      }
      cancelInsteadOfBack
    >
      <Box maxWidth={'840px'} margin="0 auto">
        <Box sx={{ width: '100%', padding: '24px', margin: '0 auto', maxWidth: '550px' }}>
          <Stepper activeStep={activeStep} sx={{ marginBottom: '24px' }}>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel>{t(`app.vod.step.${index + 1}`, label)}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Card className={classes.cardPadding}>
          {activeStep === 0 && (
            <>
              <Typography className={classes.title}>{t('app.vod.videoDetails')}</Typography>
              <ItemCreation<ExtendedVoD>
                listItems={listItems}
                headers={[]}
                onSave={handleSave}
                initialValues={initialValues}
                validate={validate}
                networkError={error}
                isLoading={isLoading}
                onValidationErrors={onValidationErrors}
              />
            </>
          )}
          {activeStep === 1 && (
            <>
              <Typography className={classes.title}>{t('app.vod.upload')}</Typography>
              <Typography className={classes.subtitle}>{t('app.vod.uploadSubtitle')}</Typography>
              <DropArea
                handleFile={handleUploadVideo}
                tooltip={t('app.vod.uploadVideoInstructions')}
                typeCheck="video"
              />
              {isUploading && (
                <div className={classes.loadingContainer}>
                  <div className={classes.loadingCar}>
                    <div
                      className={classes.loadingIndicator}
                      style={{
                        width: `${uploadProgress}%`,
                      }}
                    />
                  </div>
                  <span className={classes.uploading}>{`Upload Progress: ${uploadProgress}%`}</span>
                </div>
              )}
              {uploadError && (
                <Typography color="error" sx={{ mt: 2 }}>
                  {uploadError.message}
                </Typography>
              )}
            </>
          )}
          {activeStep === 2 && (
            <Box>
              <Typography className={classes.title}>{t('app.vod.pickThumbnail')}</Typography>
              <Typography className={classes.subtitle}>
                {t('app.vod.pickThumbnailSubtitle')}
              </Typography>

              {videoId && (
                <VoDPreview
                  onSaveThumbnail={handleSaveThumbnail}
                  isEdit
                  videoId={videoId}
                  thumbnailUrl={thumbnailUrlFromResponse}
                />
              )}
            </Box>
          )}
        </Card>
      </Box>
    </PrivateLayout>
  );
};

export default VideoOnDemandEdit;
